import { type ReactNode } from 'react'
import { ButtonPrimary } from '../buttonPrimary'
import { ActionTooltip } from '../actionTooltip'
import { FloatingActionPortal } from '../floatingActionPortal'
import styles from './SignatureActionBar.module.scss'

export type SignatureActionBarProps = {
    onSubmit: () => void
    /** Dims Submit and, on hover, explains what's missing via `blockedMessage`. */
    isSubmitBlocked?: boolean
    blockedMessage?: ReactNode
    isSubmitLoading?: boolean
    /** "1 of 1 confirmed" progress copy, shown to the left of the button. */
    consentProgressLabel?: string
    /** Toggles the Unsigned / Signed status above the consent progress label. */
    hasSignature?: boolean
    /** Bolds the consent progress label once every required checkbox is checked. */
    allConsentsConfirmed?: boolean
    submitLabel?: string
    /** Replaces submitLabel beside the spinner while isSubmitLoading */
    submitLoadingLabel?: string
}

/**
 * Fixed bottom floating pill for the signature step: Unsigned/Signed status,
 * consent progress, and Submit. Cloned from @sentinel-legal/forms SignatureActionBar.
 */
export const SignatureActionBar = ({
    onSubmit,
    isSubmitBlocked = false,
    blockedMessage = null,
    isSubmitLoading = false,
    consentProgressLabel,
    hasSignature = false,
    allConsentsConfirmed = false,
    submitLabel = 'Submit',
    submitLoadingLabel = 'Submitting…',
}: SignatureActionBarProps) => (
    <FloatingActionPortal>
        <div className={styles.root}>
            <div className={styles.pill}>
                {consentProgressLabel ? (
                    <div className={styles.status}>
                        {hasSignature ? (
                            <span className={styles.statusLabelSigned}>Signed</span>
                        ) : (
                            <span className={styles.statusLabel}>Unsigned</span>
                        )}
                        {allConsentsConfirmed ? (
                            <span className={styles.progressConfirmed}>{consentProgressLabel}</span>
                        ) : (
                            <span className={styles.progress}>{consentProgressLabel}</span>
                        )}
                    </div>
                ) : null}
                <ActionTooltip content={blockedMessage} enabled={isSubmitBlocked}>
                    <ButtonPrimary
                        onClick={onSubmit}
                        blocked={isSubmitBlocked}
                        loading={isSubmitLoading}
                        className={styles.submitButton}
                    >
                        {isSubmitLoading ? submitLoadingLabel : submitLabel}
                    </ButtonPrimary>
                </ActionTooltip>
            </div>
        </div>
    </FloatingActionPortal>
)
